import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import type { Evento, Rol, RolEvento, Usuario } from '@eventos-cien/shared';
import { Button } from '../components/Button';
import { Callout } from '../components/Callout';
import { Card } from '../components/Card';
import { Chip } from '../components/Chip';
import { Eyebrow } from '../components/Eyebrow';
import { Modal } from '../components/Modal';
import { CargaPagina } from '../components/Spinner';
import { TextField } from '../components/TextField';
import { db } from '../lib/firebase';
import { apiAuth, ApiError } from '../lib/apiAuth';

interface UsuarioConId extends Usuario {
  uid: string;
}

interface EventoConId extends Evento {
  eventoId: string;
}

/** Cuenta de Firebase Auth sin alta en `usuarios` — sugerencia de autocompletado, nunca del listado principal. */
interface AuthDisponible {
  uid: string;
  email: string;
  nombre: string | null;
}

type EstadoModal =
  | { tipo: 'crear' }
  | { tipo: 'editar'; usuario: UsuarioConId; foco?: 'password' };

const ROLES: Rol[] = ['admin', 'asesor', 'staff'];
const ETIQUETA_ROL: Record<Rol, string> = { admin: 'Admin', asesor: 'Asesor', staff: 'Staff' };

/** Colores semanticos por rol, para leer la jerarquia de un vistazo en listas grandes. */
const COLOR_ROL: Record<Rol, string> = {
  admin: 'bg-purple-50 text-purple-700',
  asesor: 'bg-blue-50 text-blue-700',
  staff: 'bg-orange-50 text-orange-700',
};

/** Solo asesor/staff se vinculan a eventos — admin es una capacidad global. */
function rolesDeEvento(roles: Rol[]): RolEvento[] {
  return roles.filter((r): r is RolEvento => r === 'asesor' || r === 'staff');
}

function IconoEslabon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M9 15l6-6" />
      <path d="M11 6l1-1a3.5 3.5 0 015 5l-1 1" />
      <path d="M13 18l-1 1a3.5 3.5 0 01-5-5l1-1" />
    </svg>
  );
}

function IconoPuntos() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
      <circle cx="12" cy="5" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="12" cy="19" r="1.8" />
    </svg>
  );
}

function IconoOjo() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="2.5" />
    </svg>
  );
}

function IconoOjoTachado() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M3 3l18 18" />
      <path d="M10.6 5.6A10.6 10.6 0 0112 5.5c6 0 9.5 6.5 9.5 6.5a13.6 13.6 0 01-3 3.6M6.3 6.9C4 8.6 2.5 12 2.5 12s3.5 6.5 9.5 6.5a9.9 9.9 0 004.2-.9" />
      <path d="M9.9 9.9a2.5 2.5 0 003.5 3.5" />
    </svg>
  );
}

function BotonMostrarPassword({ visible, onClick }: { visible: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
      className="rounded p-1 text-texto/40 hover:text-texto"
      onClick={onClick}
    >
      {visible ? <IconoOjoTachado /> : <IconoOjo />}
    </button>
  );
}

/** Lista de checkboxes de eventos activos, compartida por alta y edicion. */
function ListaEventos({
  eventos,
  seleccionados,
  onToggle,
}: {
  eventos: EventoConId[];
  seleccionados: string[];
  onToggle: (eventoId: string) => void;
}) {
  return (
    <div className="max-h-36 overflow-y-auto rounded-lg border border-borde">
      {eventos.map((ev) => (
        <label
          key={ev.eventoId}
          className="flex items-center gap-2 border-b border-borde px-3 py-2 text-sm last:border-b-0"
        >
          <input
            type="checkbox"
            className="h-4 w-4 shrink-0 rounded border-borde text-navy focus:ring-navy/30"
            checked={seleccionados.includes(ev.eventoId)}
            onChange={() => onToggle(ev.eventoId)}
          />
          <span className="truncate text-texto">{ev.nombre}</span>
        </label>
      ))}
    </div>
  );
}

export function UsuariosScreen() {
  const [usuarios, setUsuarios] = useState<UsuarioConId[] | null>(null);
  const [eventos, setEventos] = useState<EventoConId[]>([]);
  const [q, setQ] = useState('');
  const [filtroRol, setFiltroRol] = useState<Rol | 'todos'>('todos');
  const [modal, setModal] = useState<EstadoModal | null>(null);
  const [uidMenuAbierto, setUidMenuAbierto] = useState<string | null>(null);
  const [uidLinkAbierto, setUidLinkAbierto] = useState<string | null>(null);
  const [uidCambiandoActivo, setUidCambiandoActivo] = useState<string | null>(null);
  const [msgGlobal, setMsgGlobal] = useState<{ tono: 'exito' | 'error'; texto: string } | null>(
    null,
  );
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(
    () =>
      onSnapshot(query(collection(db, 'usuarios'), orderBy('creadoEn', 'desc')), (s) =>
        setUsuarios(s.docs.map((d) => ({ ...(d.data() as Usuario), uid: d.id }))),
      ),
    [],
  );

  useEffect(
    () =>
      onSnapshot(collection(db, 'eventos'), (s) =>
        setEventos(
          s.docs
            .map((d) => ({ eventoId: d.id, ...(d.data() as Evento) }))
            .filter((e) => e.estado === 'activo'),
        ),
      ),
    [],
  );

  useEffect(() => {
    if (!uidMenuAbierto && !uidLinkAbierto) return;
    function onDocClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setUidMenuAbierto(null);
        setUidLinkAbierto(null);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [uidMenuAbierto, uidLinkAbierto]);

  const filtrados = useMemo(() => {
    if (!usuarios) return [];
    const norm = q.trim().toLowerCase();
    return usuarios.filter((u) => {
      if (filtroRol !== 'todos' && !u.roles.includes(filtroRol)) return false;
      if (norm && !u.nombre.toLowerCase().includes(norm) && !u.email.toLowerCase().includes(norm))
        return false;
      return true;
    });
  }, [usuarios, q, filtroRol]);

  async function toggleActivo(u: UsuarioConId) {
    setMsgGlobal(null);
    setUidCambiandoActivo(u.uid);
    try {
      await apiAuth(`/admin/usuarios/${u.uid}/activo`, {
        method: 'PATCH',
        body: JSON.stringify({ activo: !u.activo }),
      });
    } catch (err) {
      setMsgGlobal({
        tono: 'error',
        texto: err instanceof ApiError ? err.message : 'No se pudo actualizar',
      });
    } finally {
      setUidCambiandoActivo(null);
      setUidMenuAbierto(null);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Eyebrow>Administración</Eyebrow>
          <h1 className="mt-1 text-xl font-semibold text-texto">Usuarios</h1>
        </div>
        <Button onClick={() => setModal({ tipo: 'crear' })}>Nuevo usuario</Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="sm:max-w-150 sm:flex-1">
          <TextField
            label="Buscar"
            type="search"
            placeholder="Nombre o correo"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 sm:shrink-0 sm:pb-0">
          <Chip activo={filtroRol === 'todos'} onClick={() => setFiltroRol('todos')}>
            Todos
          </Chip>
          {ROLES.map((r) => (
            <Chip key={r} activo={filtroRol === r} onClick={() => setFiltroRol(r)}>
              {ETIQUETA_ROL[r]}
            </Chip>
          ))}
        </div>
      </div>

      {msgGlobal && <Callout tono={msgGlobal.tono}>{msgGlobal.texto}</Callout>}

      {usuarios === null ? (
        <CargaPagina texto="Cargando usuarios" />
      ) : (
        <ul className="space-y-2">
          {filtrados.map((u) => {
            const rolesEvento = rolesDeEvento(u.roles);
            const abrioMenu = uidMenuAbierto === u.uid;
            const abrioLink = uidLinkAbierto === u.uid;
            return (
              <li key={u.uid}>
                <Card>
                  <div className="flex items-center justify-between gap-3 p-4">
                    <span className="min-w-0">
                      <strong className="block truncate text-sm text-texto">{u.nombre}</strong>
                      <span className="block truncate text-xs text-texto/50">{u.email}</span>
                    </span>
                    <span
                      className="flex shrink-0 flex-wrap items-center justify-end gap-1.5"
                      ref={abrioMenu || abrioLink ? popoverRef : undefined}
                    >
                      {!u.activo && (
                        <span className="rounded-full bg-texto/5 px-2.5 py-0.5 text-xs font-medium text-texto/50">
                          inactivo
                        </span>
                      )}
                      {u.roles.map((r) => (
                        <span
                          key={r}
                          className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${COLOR_ROL[r]}`}
                        >
                          {ETIQUETA_ROL[r]}
                        </span>
                      ))}
                      {rolesEvento.length > 0 && (
                        <BotonVincular
                          usuario={u}
                          rolesEvento={rolesEvento}
                          eventos={eventos}
                          abierto={abrioLink}
                          onToggle={() => {
                            setUidLinkAbierto(abrioLink ? null : u.uid);
                            setUidMenuAbierto(null);
                          }}
                        />
                      )}
                      <MenuAcciones
                        usuario={u}
                        abierto={abrioMenu}
                        cambiandoActivo={uidCambiandoActivo === u.uid}
                        onToggle={() => {
                          setUidMenuAbierto(abrioMenu ? null : u.uid);
                          setUidLinkAbierto(null);
                        }}
                        onEditar={() => {
                          setModal({ tipo: 'editar', usuario: u });
                          setUidMenuAbierto(null);
                        }}
                        onPassword={() => {
                          setModal({ tipo: 'editar', usuario: u, foco: 'password' });
                          setUidMenuAbierto(null);
                        }}
                        onToggleActivo={() => void toggleActivo(u)}
                      />
                    </span>
                  </div>
                </Card>
              </li>
            );
          })}
          {filtrados.length === 0 && <li className="text-sm text-texto/50">Sin resultados.</li>}
        </ul>
      )}

      {modal?.tipo === 'crear' && (
        <Modal titulo="Nuevo usuario" onClose={() => setModal(null)}>
          <FormularioUsuario
            eventos={eventos}
            onCreado={() => setModal(null)}
            onCancelar={() => setModal(null)}
          />
        </Modal>
      )}
      {modal?.tipo === 'editar' && (
        <Modal titulo="Editar usuario" onClose={() => setModal(null)}>
          <DetalleUsuario
            usuario={modal.usuario}
            eventos={eventos}
            foco={modal.foco}
            onCerrar={() => setModal(null)}
          />
        </Modal>
      )}
    </div>
  );
}

function BotonVincular({
  usuario,
  rolesEvento,
  eventos,
  abierto,
  onToggle,
}: {
  usuario: UsuarioConId;
  rolesEvento: RolEvento[];
  eventos: EventoConId[];
  abierto: boolean;
  onToggle: () => void;
}) {
  const [enviando, setEnviando] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function vincular(eventoId: string, nombreEvento: string) {
    setEnviando(eventoId);
    setMsg(null);
    try {
      await apiAuth(`/admin/eventos/${eventoId}/miembros/${usuario.uid}`, {
        method: 'PATCH',
        body: JSON.stringify({ roles: rolesEvento, activo: true }),
      });
      setMsg(`Vinculado a ${nombreEvento}.`);
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'No se pudo vincular');
    } finally {
      setEnviando(null);
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={`Vincular a ${usuario.nombre} a un evento`}
        aria-haspopup="menu"
        aria-expanded={abierto}
        className="rounded-full p-1.5 text-texto/50 hover:bg-navy/5 hover:text-navy"
        onClick={onToggle}
      >
        <IconoEslabon />
      </button>
      {abierto && (
        <div className="absolute right-0 top-full z-10 mt-1 w-56 overflow-hidden rounded-lg border border-borde bg-white text-sm shadow-md">
          <p className="border-b border-borde px-3 py-2 text-xs font-medium text-texto/50">
            Vincular a evento activo
          </p>
          {eventos.length === 0 ? (
            <p className="px-3 py-2 text-xs text-texto/50">No hay eventos activos.</p>
          ) : (
            <ul>
              {eventos.map((ev) => (
                <li key={ev.eventoId}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-navy/5 disabled:opacity-50"
                    disabled={enviando === ev.eventoId}
                    onClick={() => void vincular(ev.eventoId, ev.nombre)}
                  >
                    <span className="truncate">{ev.nombre}</span>
                    {enviando === ev.eventoId && (
                      <span className="h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-navy/30 border-t-navy" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {msg && <p className="border-t border-borde px-3 py-2 text-xs text-texto/70">{msg}</p>}
        </div>
      )}
    </div>
  );
}

function MenuAcciones({
  usuario,
  abierto,
  cambiandoActivo,
  onToggle,
  onEditar,
  onPassword,
  onToggleActivo,
}: {
  usuario: UsuarioConId;
  abierto: boolean;
  cambiandoActivo: boolean;
  onToggle: () => void;
  onEditar: () => void;
  onPassword: () => void;
  onToggleActivo: () => void;
}) {
  return (
    <div className="relative">
      <button
        type="button"
        aria-label={`Más acciones para ${usuario.nombre}`}
        aria-haspopup="menu"
        aria-expanded={abierto}
        className="rounded-full p-1.5 text-texto/50 hover:bg-navy/5 hover:text-navy"
        onClick={onToggle}
      >
        <IconoPuntos />
      </button>
      {abierto && (
        <div
          role="menu"
          className="absolute right-0 top-full z-10 mt-1 w-48 overflow-hidden rounded-lg border border-borde bg-white text-sm shadow-md"
        >
          <button
            type="button"
            role="menuitem"
            className="block w-full px-3.5 py-2 text-left text-texto hover:bg-navy/5"
            onClick={onEditar}
          >
            Editar
          </button>
          <button
            type="button"
            role="menuitem"
            className="block w-full px-3.5 py-2 text-left text-texto hover:bg-navy/5"
            onClick={onPassword}
          >
            Restablecer contraseña
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={cambiandoActivo}
            className="block w-full px-3.5 py-2 text-left text-red-600 hover:bg-red-50 disabled:opacity-50"
            onClick={onToggleActivo}
          >
            {usuario.activo ? 'Desactivar perfil' : 'Activar perfil'}
          </button>
        </div>
      )}
    </div>
  );
}

function FormularioUsuario({
  eventos,
  onCreado,
  onCancelar,
}: {
  eventos: EventoConId[];
  onCreado: () => void;
  onCancelar: () => void;
}) {
  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [password, setPassword] = useState('');
  const [verPassword, setVerPassword] = useState(false);
  const [roles, setRoles] = useState<Rol[]>(['asesor']);
  const [eventosSeleccionados, setEventosSeleccionados] = useState<string[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<{ tono: 'exito' | 'error'; texto: string } | null>(null);

  const [sugerencias, setSugerencias] = useState<AuthDisponible[]>([]);
  const [seleccionado, setSeleccionado] = useState<AuthDisponible | null>(null);
  const idPeticion = useRef(0);

  const rolesEvento = rolesDeEvento(roles);

  useEffect(() => {
    if (seleccionado) return; // ya se eligio una sugerencia para este correo, no vuelvas a buscar
    const q = email.trim();
    if (q.length < 2) {
      setSugerencias([]);
      return;
    }
    const yo = ++idPeticion.current;
    const t = setTimeout(() => {
      apiAuth<AuthDisponible[]>(`/admin/usuarios/auth-disponibles?q=${encodeURIComponent(q)}`)
        .then((r) => {
          if (idPeticion.current === yo) setSugerencias(r);
        })
        .catch(() => {
          if (idPeticion.current === yo) setSugerencias([]);
        });
    }, 300);
    return () => clearTimeout(t);
  }, [email, seleccionado]);

  useEffect(() => {
    if (rolesEvento.length === 0) setEventosSeleccionados([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo depende de si hay rol de evento, no de la lista completa
  }, [rolesEvento.length]);

  function elegirSugerencia(u: AuthDisponible) {
    setEmail(u.email);
    if (u.nombre) setNombre(u.nombre);
    setSeleccionado(u);
    setSugerencias([]);
  }

  function toggleRol(r: Rol) {
    setRoles((rs) => (rs.includes(r) ? rs.filter((x) => x !== r) : [...rs, r]));
  }

  function toggleEvento(eventoId: string) {
    setEventosSeleccionados((es) =>
      es.includes(eventoId) ? es.filter((x) => x !== eventoId) : [...es, eventoId],
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    if (roles.length === 0) {
      setMsg({ tono: 'error', texto: 'Elige al menos un rol' });
      return;
    }
    setEnviando(true);
    try {
      const r = await apiAuth<{ uid: string; creado: boolean }>('/admin/usuarios', {
        method: 'POST',
        body: JSON.stringify({ email, nombre, roles, password: password || undefined }),
      });
      if (eventosSeleccionados.length > 0 && rolesEvento.length > 0) {
        await Promise.all(
          eventosSeleccionados.map((eventoId) =>
            apiAuth(`/admin/eventos/${eventoId}/miembros/${r.uid}`, {
              method: 'PATCH',
              body: JSON.stringify({ roles: rolesEvento, activo: true }),
            }),
          ),
        );
      }
      setMsg({
        tono: 'exito',
        texto: r.creado
          ? `Usuario ${nombre} creado en Firebase Auth y enlazado.`
          : `Ya existía una cuenta con ese correo: se enlazó sin tocar su contraseña.`,
      });
      setEmail('');
      setNombre('');
      setPassword('');
      setSeleccionado(null);
      setEventosSeleccionados([]);
      onCreado();
    } catch (err) {
      setMsg({ tono: 'error', texto: err instanceof ApiError ? err.message : 'No se pudo crear' });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={onSubmit}>
      <TextField label="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
      <div className="relative flex flex-col gap-1.5">
        <TextField
          label="Correo"
          type="email"
          autoCapitalize="off"
          autoCorrect="off"
          autoComplete="off"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setSeleccionado(null);
          }}
          required
        />
        {sugerencias.length > 0 && (
          <ul className="absolute top-full z-10 mt-1 w-full overflow-hidden rounded-lg border border-borde bg-white text-sm shadow-md">
            {sugerencias.map((u) => (
              <li key={u.uid}>
                <button
                  type="button"
                  className="block w-full px-3.5 py-2 text-left hover:bg-navy/5"
                  onClick={() => elegirSugerencia(u)}
                >
                  <span className="block truncate text-texto">{u.nombre || u.email}</span>
                  {u.nombre && (
                    <span className="block truncate text-xs text-texto/50">{u.email}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {seleccionado ? (
        <p className="text-xs text-texto/50">
          Esa cuenta ya existe en Auth: se enlazará sin tocar su contraseña actual.
        </p>
      ) : (
        <TextField
          label="Contraseña inicial"
          type={verPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="Solo aplica si el correo es nuevo. Si ya existe una cuenta, se enlaza sin tocarla."
          trailing={
            <BotonMostrarPassword
              visible={verPassword}
              onClick={() => setVerPassword((v) => !v)}
            />
          }
        />
      )}
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-texto">Roles</span>
        <div className="flex gap-2">
          {ROLES.map((r) => (
            <Chip key={r} activo={roles.includes(r)} onClick={() => toggleRol(r)}>
              {ETIQUETA_ROL[r]}
            </Chip>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-texto">
          Asignar a evento(s) <span className="font-normal text-texto/40">(opcional)</span>
        </span>
        {rolesEvento.length === 0 ? (
          <p className="text-xs text-texto/50">
            Elige el rol Asesor o Staff para asignarlo a un evento desde aquí.
          </p>
        ) : eventos.length === 0 ? (
          <p className="text-xs text-texto/50">No hay eventos activos ahora mismo.</p>
        ) : (
          <ListaEventos
            eventos={eventos}
            seleccionados={eventosSeleccionados}
            onToggle={toggleEvento}
          />
        )}
      </div>

      {msg && <Callout tono={msg.tono}>{msg.texto}</Callout>}
      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variante="secundario" onClick={onCancelar}>
          Cancelar
        </Button>
        <Button type="submit" cargando={enviando}>
          Guardar usuario
        </Button>
      </div>
    </form>
  );
}

function DetalleUsuario({
  usuario,
  eventos,
  foco,
  onCerrar,
}: {
  usuario: UsuarioConId;
  eventos: EventoConId[];
  foco?: 'password';
  onCerrar: () => void;
}) {
  const [roles, setRoles] = useState<Rol[]>(usuario.roles);
  const [activo, setActivo] = useState(usuario.activo);
  const [guardandoRoles, setGuardandoRoles] = useState(false);
  const [guardandoActivo, setGuardandoActivo] = useState(false);
  const [nuevaPassword, setNuevaPassword] = useState('');
  const [verPassword, setVerPassword] = useState(false);
  const [cambiandoPassword, setCambiandoPassword] = useState(false);
  const [eventosSeleccionados, setEventosSeleccionados] = useState<string[]>([]);
  const [vinculando, setVinculando] = useState(false);
  const [msg, setMsg] = useState<{ tono: 'exito' | 'error'; texto: string } | null>(null);
  const passwordRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    if (foco === 'password') passwordRef.current?.scrollIntoView({ block: 'center' });
  }, [foco]);

  const rolesEvento = rolesDeEvento(roles);

  function toggleRol(r: Rol) {
    setRoles((rs) => (rs.includes(r) ? rs.filter((x) => x !== r) : [...rs, r]));
  }

  function toggleEvento(eventoId: string) {
    setEventosSeleccionados((es) =>
      es.includes(eventoId) ? es.filter((x) => x !== eventoId) : [...es, eventoId],
    );
  }

  async function guardarRoles() {
    setMsg(null);
    setGuardandoRoles(true);
    try {
      await apiAuth(`/admin/usuarios/${usuario.uid}/roles`, {
        method: 'PATCH',
        body: JSON.stringify({ roles }),
      });
      setMsg({ tono: 'exito', texto: 'Roles actualizados.' });
    } catch (err) {
      setMsg({ tono: 'error', texto: err instanceof ApiError ? err.message : 'No se pudo guardar' });
    } finally {
      setGuardandoRoles(false);
    }
  }

  async function toggleActivo() {
    setMsg(null);
    setGuardandoActivo(true);
    try {
      await apiAuth(`/admin/usuarios/${usuario.uid}/activo`, {
        method: 'PATCH',
        body: JSON.stringify({ activo: !activo }),
      });
      setActivo((a) => !a);
    } catch (err) {
      setMsg({ tono: 'error', texto: err instanceof ApiError ? err.message : 'No se pudo actualizar' });
    } finally {
      setGuardandoActivo(false);
    }
  }

  async function resetearPassword(e: FormEvent) {
    e.preventDefault();
    if (nuevaPassword.length < 6) {
      setMsg({ tono: 'error', texto: 'La contraseña debe tener al menos 6 caracteres.' });
      return;
    }
    setMsg(null);
    setCambiandoPassword(true);
    try {
      await apiAuth(`/admin/usuarios/${usuario.uid}/password`, {
        method: 'POST',
        body: JSON.stringify({ password: nuevaPassword }),
      });
      setMsg({ tono: 'exito', texto: 'Contraseña reseteada.' });
      setNuevaPassword('');
    } catch (err) {
      setMsg({ tono: 'error', texto: err instanceof ApiError ? err.message : 'No se pudo resetear' });
    } finally {
      setCambiandoPassword(false);
    }
  }

  async function vincularEventos() {
    if (eventosSeleccionados.length === 0) return;
    setMsg(null);
    setVinculando(true);
    try {
      await Promise.all(
        eventosSeleccionados.map((eventoId) =>
          apiAuth(`/admin/eventos/${eventoId}/miembros/${usuario.uid}`, {
            method: 'PATCH',
            body: JSON.stringify({ roles: rolesEvento, activo: true }),
          }),
        ),
      );
      setMsg({ tono: 'exito', texto: 'Vinculado a los eventos seleccionados.' });
      setEventosSeleccionados([]);
    } catch (err) {
      setMsg({ tono: 'error', texto: err instanceof ApiError ? err.message : 'No se pudo vincular' });
    } finally {
      setVinculando(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-texto">Roles globales</span>
        <div className="flex flex-wrap items-center gap-2">
          {ROLES.map((r) => (
            <Chip key={r} activo={roles.includes(r)} onClick={() => toggleRol(r)}>
              {ETIQUETA_ROL[r]}
            </Chip>
          ))}
          <Button variante="secundario" cargando={guardandoRoles} onClick={guardarRoles}>
            Guardar roles
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-texto">
          {activo ? 'Cuenta activa' : 'Cuenta inactiva'}
        </span>
        <Button variante="secundario" cargando={guardandoActivo} onClick={() => void toggleActivo()}>
          {activo ? 'Desactivar' : 'Activar'}
        </Button>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-texto">Asignar a evento(s)</span>
        {rolesEvento.length === 0 ? (
          <p className="text-xs text-texto/50">Este usuario no tiene el rol Asesor ni Staff.</p>
        ) : eventos.length === 0 ? (
          <p className="text-xs text-texto/50">No hay eventos activos ahora mismo.</p>
        ) : (
          <>
            <ListaEventos
              eventos={eventos}
              seleccionados={eventosSeleccionados}
              onToggle={toggleEvento}
            />
            <Button
              variante="secundario"
              cargando={vinculando}
              disabled={eventosSeleccionados.length === 0}
              onClick={() => void vincularEventos()}
              className="self-start"
            >
              Vincular seleccionados
            </Button>
          </>
        )}
      </div>

      <form ref={passwordRef} className="flex flex-col gap-3" onSubmit={resetearPassword}>
        <span className="text-sm font-medium text-texto">Resetear contraseña</span>
        <TextField
          label="Contraseña nueva"
          type={verPassword ? 'text' : 'password'}
          value={nuevaPassword}
          onChange={(e) => setNuevaPassword(e.target.value)}
          trailing={
            <BotonMostrarPassword
              visible={verPassword}
              onClick={() => setVerPassword((v) => !v)}
            />
          }
        />
        <Button type="submit" variante="secundario" cargando={cambiandoPassword}>
          Resetear
        </Button>
      </form>

      {msg && <Callout tono={msg.tono}>{msg.texto}</Callout>}

      <div className="flex justify-end pt-1">
        <Button type="button" variante="secundario" onClick={onCerrar}>
          Cerrar
        </Button>
      </div>
    </div>
  );
}
